<template>
  <div class="right-side-top-container">
    <div>
      <h4>{{ t("currentOrder") }}</h4>
      <div>
        <button
          :class="['btn', 'btn-fiery-red', { 'btn-disabled': !canCheckout }]"
          :disabled="!canCheckout"
          @click="handleClearCart"
        >
          {{ t("clearAll") }}
        </button>
        <button class="btn btn-fiery-red close-cart-btn" @click="closeCart">
          <IconCloseLarge :width="15" :height="17" fill="#262626" />
        </button>
      </div>
    </div>
    <div>
      <button class="table-modal-btn" @click="openTableModal">
        {{ tableText }}
      </button>
      <p :class="{ 'price-updated': animateAmount }">
        {{ t("totalItems") }}:
        {{ $customTextFormatter.formattedNumber(totalItems, "0") }}
      </p>
    </div>
  </div>
  <div class="scrollable-container" ref="containerRef">
    <div class="scrollable-content" ref="contentRef">
      <div v-if="orderItems.length === 0" class="empty-order">
        <p>{{ t("noItemsInOrderYet") }}</p>
      </div>
      <transition-group
        tag="div"
        class="order-list-container"
        name="order-list"
      >
        <div class="order-card" v-for="item in orderItems" :key="item.id">
          <img
            :src="
              item.food.image_url
                ? proxy.$customImageUtil.getImageUrl(item.food.image_url)
                : DEFAULT_PRODUCT_IMAGE
            "
            width="207"
            height="216"
            :alt="item.food.name"
            class="ordered-food-img"
          />
          <div class="order-card-content">
            <div class="order-title">
              <h3>{{ item.food.name }}</h3>
              <div class="order-price">
                <IconCurrencyBaht
                  :width="12"
                  :height="18"
                  stroke="#262626"
                  :stroke-width="2"
                />
                <span>{{ item.price.toFixed(2) }}</span>
              </div>
            </div>
            <div class="order-price-quantity">
              <div class="meta-container">
                <div
                  class="order-meta"
                  v-for="(value, key) in item.options"
                  :key="key"
                >
                  <span>{{ key }}:</span>
                  <span>{{ value.option || "N/A" }}</span>
                </div>
              </div>
              <div class="food-counter-container">
                <div class="food-counter remove-cart-item">
                  <div class="decrease" @click="decreaseQuantity(item.id)">
                    <IconMinus
                      :width="24"
                      :height="24"
                      stroke="currentColor"
                      :stroke-width="1.5"
                    />
                  </div>
                  <input
                    type="number"
                    v-model.number="item.quantity"
                    class="count"
                    readonly
                  />
                  <div class="increase" @click="increaseQuantity(item.id)">
                    <IconPlus
                      :width="24"
                      :height="24"
                      stroke="currentColor"
                      :stroke-width="1.5"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition-group>
    </div>
    <CustomScroller
      ref="customScroller"
      :content-ref="contentRef"
      :container-ref="containerRef"
    />
  </div>

  <!-- Payment Component -->
  <PaymentTab @update:openTable="isTableModalOpen = true" />

  <!-- Modals -->
  <TableModal
    :is-open="isTableModalOpen"
    @update:isOpen="isTableModalOpen = $event"
  />
  <CheckoutModal
    :is-open="openCheckoutModal"
    @update:isOpen="openCheckoutModal = $event"
  />
</template>

<script setup>
import {
  computed,
  defineEmits,
  getCurrentInstance,
  ref,
  onMounted,
  onBeforeUnmount,
} from "vue";
import eventBus from "@/eventBus";
import { useI18n } from "vue-i18n";
import { useAnimatedValue } from "@/composables/useAnimatedValue";
import { useCheckoutState } from "@/composables/useCheckoutState";
import { useOrderStore } from "@/stores/order";
import { usePanelScroller } from "@/composables/usePanelScroller";
import { useProduct } from "@/composables/useProduct";
import { useOrderGuard } from "@/composables/useOrderGuard";
import { useBroadcastComposable } from "@/composables/useBroadcastComposable";
import IconCloseLarge from "@/components/icons/iconCloseLarge.vue";
import IconMinus from "@/components/icons/iconMinus.vue";
import IconPlus from "@/components/icons/iconPlus.vue";
import IconCurrencyBaht from "@/components/icons/iconCurrencyBaht.vue";
import CustomScroller from "@/components/order/customScroller.vue";
import PaymentTab from "@/components/order/paymentTab.vue";
import CheckoutModal from "@/components/order/modals/checkoutModal.vue";
import TableModal from "@/components/order/modals/tableModal.vue";
// import { DEFAULT_TABLE_IMAGE, DEFAULT_PRODUCT_IMAGE } from "@/constants/image";
import { STATUS_PROCESSING } from "@/constants/order";
import Swal from "sweetalert2";

const emit = defineEmits(["close-cart"]);

const { proxy } = getCurrentInstance();
const { t } = useI18n();

const { broadcastOrderToCds } = useBroadcastComposable();
const { calculateProductPrice } = useProduct();
const {
  getCurrentTable,
  getOrderItems,
  getOrderStatus,
  getTotalAmount,
  getTotalItems,
  updateOrderItems,
  resetOrder,
} = useOrderStore();

const orderItems = computed(() => getOrderItems());
const currentTable = computed(() => getCurrentTable());
const totalAmount = computed(() => getTotalAmount());
const totalItems = computed(() => getTotalItems());

const [, animateAmount] = useAnimatedValue(totalAmount);

const isTableModalOpen = ref(false);

// Default order broadcast delay
const DEFAULT_ORDER_BROADCAST_DELAY = 10;

// Final broadcast delay of order to CDS if order placed and has the status "processing" in "order" state for "orderStatus"
const FINAL_ORDER_BROADCAST_DELAY = 10000;

// Use the new panel scroller composable
const { containerRef, contentRef, customScroller, initializeWithRetry } =
  usePanelScroller();

const { canCheckout, openCheckoutModal, setOpenCheckoutModal } =
  useCheckoutState();

// Guard for order modification
const { canModifyOrder } = useOrderGuard(() => {
  setOpenCheckoutModal(true);
});

const openTableModal = () => {
  if (!canModifyOrder()) return;
  isTableModalOpen.value = true;
};

const closeCart = () => {
  emit("close-cart");
};

const tableText = computed(() => {
  const tableNumber = proxy.$customTextFormatter.formattedNumber(
    currentTable.value?.number,
    "00"
  );
  return currentTable.value
    ? `${t("table")}: ${tableNumber}`
    : t("selectTable");
});

const increaseQuantity = (orderId) => {
  if (!canModifyOrder()) return;
  const existingItems = orderItems.value.map((i) =>
    i.id === orderId
      ? {
          ...i,
          quantity: i.quantity + 1,
          price:
            (i.quantity + 1) *
            calculateProductPrice(i.food, i.options, i.extras),
        }
      : i
  );
  updateOrderItems(existingItems);
  initializeWithRetry();
  broadcastOrder();
};

const decreaseQuantity = (orderId) => {
  if (!canModifyOrder()) return;
  let existingItems = [...orderItems.value];
  const item = existingItems.find((i) => i.id === orderId);
  if (item && item.quantity > 1) {
    existingItems = existingItems.map((i) =>
      i.id === orderId
        ? {
            ...i,
            quantity: i.quantity - 1,
            price:
              (i.quantity - 1) *
              calculateProductPrice(i.food, i.options, i.extras),
          }
        : i
    );
  } else if (item && item.quantity === 1) {
    existingItems = existingItems.filter((i) => i.id !== orderId);
  }
  updateOrderItems(existingItems);
  initializeWithRetry();
  broadcastOrder();
};

const handleClearCart = () => {
  Swal.fire({
    title: "Are you sure?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes",
    confirmButtonColor: "#007bdf",
    cancelButtonColor: "#fe3c00",
  }).then((result) => {
    /* Read more about isConfirmed, isDenied below */
    if (result.isConfirmed) {
      clearCart();
    }
  });
};

const clearCart = async () => {
  if (orderItems.value.length === 0) return;

  /**
   * This check is done when order is placed and "order" in state has the value "processing" for "orderStatus".
   * This provides CDS with the latest updated values of different attributes of order.
   */
  const orderStatus = getOrderStatus();
  if (orderStatus == STATUS_PROCESSING) {
    broadcastOrder(0);
  }

  const itemsToRemove = [...orderItems.value];
  let removalPromises = [];

  // Trigger removal with slight stagger for animation
  const delay = itemsToRemove.length > 5 ? 75 : 100;
  for (let i = 0; i < itemsToRemove.length; i++) {
    removalPromises.push(
      new Promise((resolve) => {
        setTimeout(() => {
          const index = orderItems.value.findIndex(
            (item) => item.id === itemsToRemove[i].id
          );
          if (index !== -1) {
            updateOrderItems(
              orderItems.value.filter((item) => item.id !== itemsToRemove[i].id)
            );
          }
          if (i + 1 == itemsToRemove.length) {
            resetOrder();
            initializeWithRetry();
            /**
             * This is done to reset the CDS.
             * If the "order" in state has the value "processing" for "orderStatus" then
             * for the last entry make a significant delay so that the thank you page can be shown in CDS
             * before the "order" state is also reset in CDS.
             */
            broadcastOrder(
              orderStatus == STATUS_PROCESSING
                ? FINAL_ORDER_BROADCAST_DELAY
                : DEFAULT_ORDER_BROADCAST_DELAY
            );
          }
          resolve();
        }, i * delay);
      })
    );
  }
  await Promise.all(removalPromises);
};

const broadcastOrder = (delay = DEFAULT_ORDER_BROADCAST_DELAY) => {
  broadcastOrderToCds(delay);
};

// Listen for order-added event and trigger scroller re-init
onMounted(() => {
  eventBus.on("orderModified", initializeWithRetry);
  eventBus.on("clearCart", clearCart);
});

onBeforeUnmount(() => {
  eventBus.off("orderModified", initializeWithRetry);
  eventBus.off("clearCart", clearCart);
});
</script>

<style scoped>
/* Enhanced animation styles */

.order-list-enter-active,
.order-list-leave-active {
  transition: all 0.5s ease-in-out;
}

.order-list-enter-from,
.order-list-leave-to {
  opacity: 0;
  transform: translateX(100%);
  height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 0;
  border-width: 0;
}

.order-list-enter-to,
.order-list-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.order-list-move {
  transition: transform 0.5s ease-out;
}

.order-card {
  position: relative;
  transition: all 0.5s ease-in-out;
}
</style>
